import { motion } from "framer-motion";
import { ArrowRight, FileText, BarChart3, MapPin, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import featuredImage from "@/assets/mobile-economic-plan-hero.jpg";

const featuredArticle = {
  slug: "mobile-financial-empowerment-action-plan-analysis",
  tag: "Policy Analysis",
  title: "Closing Mobile's Wealth Gap: What Small Businesses Can Do to Advance the City's Financial Empowerment Plan",
  excerpt:
    "A research-backed look at Mobile, Alabama's Financial Empowerment Action Plan — the data behind it, and the concrete moves small businesses can make to help close the gap.",
  date: "Apr 14, 2026",
  readTime: "8 min read",
};

const sideArticles = [
  {
    icon: Landmark,
    tag: "Wealth Building",
    title: "Priority Zip Codes: Where Home Values Stay Below $50K",
    excerpt:
      "The report identifies 36603, 36605, and 36617 as areas with the lowest home valuations and sparsest business density — key targets for grant alignment.",
  },
  {
    icon: BarChart3,
    tag: "Entrepreneurship",
    title: "52% Black Population, But Business Ownership Doesn't Reflect It",
    excerpt:
      "Mobile's majority-Black city has a significant business ownership gap. The City's DBE programs and Contractor's College aim to close it.",
  },
  {
    icon: MapPin,
    tag: "Financial Services",
    title: "8% Unbanked, 17% Underbanked in Mobile",
    excerpt:
      "Banking disconnection remains a barrier. The Bank On coalition saw a 167% increase in new accounts between 2020–2021.",
  },
];

const FeaturedSpotlight = () => {
  return (
    <section className="py-16 border-b border-border">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-primary">
            Featured Analysis
          </span>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Featured Main Article */}
          <Link to={`/insights/${featuredArticle.slug}`} className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="group cursor-pointer"
            >
              <div className="relative h-64 md:h-80 rounded-xl overflow-hidden mb-5">
                <img
                  src={featuredImage}
                  alt="Financial Empowerment Action Plan — Mobile, Alabama"
                  className="w-full h-full object-cover"
                  width={1280}
                  height={720}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2">
                  <span className="text-xs font-semibold text-primary-foreground bg-primary px-3 py-1.5 rounded-full">
                    {featuredArticle.tag}
                  </span>
                  <span className="text-xs font-medium text-muted-foreground bg-card/80 backdrop-blur px-3 py-1.5 rounded-full border border-border">
                    City of Mobile, AL
                  </span>
                </div>
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-3 group-hover:text-primary transition-colors leading-tight">
                {featuredArticle.title}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4 max-w-2xl">
                {featuredArticle.excerpt}
              </p>
              <div className="flex items-center gap-4">
                <Button variant="link" className="px-0 text-primary font-semibold group/btn" asChild>
                  <span>
                    Read Full Analysis
                    <ArrowRight className="ml-1.5 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                  </span>
                </Button>
                <span className="text-xs text-muted-foreground">{featuredArticle.date} · {featuredArticle.readTime}</span>
              </div>
            </motion.div>
          </Link>

          {/* Side Articles */}
          <div className="lg:col-span-2 space-y-1">
            {sideArticles.map((article, i) => (
              <motion.div
                key={article.title}
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + i * 0.1 }}
                className="group cursor-pointer p-4 rounded-xl hover:bg-muted/50 transition-colors border border-transparent hover:border-border"
              >
                <div className="flex items-center gap-2 mb-2">
                  <article.icon className="h-3.5 w-3.5 text-primary" />
                  <span className="text-[11px] font-semibold tracking-wider uppercase text-primary">
                    {article.tag}
                  </span>
                </div>
                <h3 className="font-display font-semibold text-[15px] leading-snug mb-1.5 group-hover:text-primary transition-colors">
                  {article.title}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                  {article.excerpt}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedSpotlight;
